import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Radio from 'relements/build/esm/input/radio';
import Button from 'relements/build/esm/ui/button';
import File from 'relements/build/esm/input/file';
import Icon from 'relements/build/esm/ui/icon';

import Info from '@src/components/Icon/info.svg';
import ChatIconDefault from '@src/components/Icon/chat-icon-default.svg';
import ChatIconSmile from '@src/components/Icon/chat-icon-smile.svg';
import ChatIconText from '@src/components/Icon/chat-icon-text.svg';
import ChatIconPlaceHolder from '@src/components/Icon/chat-icon-placeholder.svg';
import ChatIconHelp from '@src/components/Icon/chat-icon-help.svg';
import ChatIconOutline from '@src/components/Icon/chat-icon-outline.svg';
import { CHAT_ICON_CONFIG } from '@src/containers/BusinessManager/const';

import './SDKChatIcon.scss';

export default function SDKChatIcon({
  label,
  options,
  value,
  onChange,
  primaryColor,
}) {
  // This represents currenlty selected icon type.
  const [selectedIconType, setSelectedIconType] = useState(value);
  const containerRef = React.useRef();
  // Handle radio change.
  const onRadioChange = index => {
    const newType = {
      type: options[index].type,
      data: {
        imageURL: '',
      },
    };
    setSelectedIconType(newType);
    onChange(newType);
  };

  // Handle file uplaod event
  const onUrlChange = url => {
    const newType = {
      ...selectedIconType,
      data: {
        imageURL: url
      },
    };
    setSelectedIconType(newType);
    onChange(newType);
  };

  /**
   * This method will return icon based on type
   * @param {string} type type of chat icon
   */
  const getChatIcon = React.useCallback(type => {
    switch (type) {
      case CHAT_ICON_CONFIG.IMAGE:
        return ChatIconPlaceHolder;
      case CHAT_ICON_CONFIG.TEXT:
        return ChatIconText;
      case CHAT_ICON_CONFIG.SMILE:
        return ChatIconSmile;
      case CHAT_ICON_CONFIG.HELP:
        return ChatIconHelp;
      default:
        return ChatIconDefault;
    }
  }, []);

  React.useEffect(() => {
    if (containerRef && containerRef.current) {
      containerRef.current.style.setProperty('--primary-color', primaryColor);
    }
  }, [primaryColor]);

  return (
    <div>
      <div className="chat-icon-label">{label}</div>
      <div className="chat-icons-container" ref={containerRef}>
        {options.map((item, index) => {
          const IconComponent = getChatIcon(item.type);
          return (
            <div className="chat-icon">
              {/* Radio and tooltip */}
              <div className="chat-icon-radio">
                <Radio.Item
                  label={item.label}
                  className="chat-icon-radio-item"
                  value={item.type === selectedIconType.type}
                  onChange={() => onRadioChange(index)}
                />
                {item.tooltip && (
                  <Icon
                    className="chat-icon-radio-tooltip"
                    src={Info}
                    tooltip={item.tooltip}
                    tooltipPosition="TOP"
                  />
                )}
              </div>
              {/* Image Container */}
              <div className="chat-icon-image">
                <div className="chat-icon-image-container">
                  {item.type === CHAT_ICON_CONFIG.IMAGE &&
                  selectedIconType.data.imageURL ? (
                    <div>
                      <div
                        className="chat-icon-image-upload"
                        style={{
                          backgroundImage: `url(${
                            selectedIconType.data.imageURL
                          })`,
                        }}
                      />
                      <ChatIconOutline />
                    </div>
                  ) : (
                    <IconComponent className="chat-icon-image-container-item" />
                  )}
                </div>
              </div>
              {/* Upload button for image and gif */}
              {item.type === CHAT_ICON_CONFIG.IMAGE && (
                <File
                  onChange={newImageURL => {
                    onUrlChange(newImageURL);
                  }}
                  type=".png, .jpeg, .gif"
                  maxFileSize={1}
                >
                  {(uploads, renderInput) => {
                    const hide = selectedIconType.type !== 'image';
                    return (
                      <div className="chat-icon-upload">
                        <Button
                          className="chat-icon-upload-button"
                          disabled={hide}
                        >
                          Upload Here
                        </Button>
                        {!hide && renderInput()}
                      </div>
                    );
                  }}
                </File>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

SDKChatIcon.defaultProps = {
  onChange: () => {},
  label: '',
  options: [],
  value: {
    type: 'default',
    data: {
      imageURL: '',
    },
  },
  primaryColor: '#0067DD',
};

SDKChatIcon.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.arrayOf({
    label: PropTypes.string,
    tooltip: PropTypes.string,
    type: PropTypes.string,
  }),
  value: PropTypes.shape({
    type: PropTypes.string,
    data: PropTypes.shape({
      imageURL: PropTypes.string
    }),
  }),
  primaryColor: PropTypes.string,
};
